import { useEffect, useRef, useState } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';

import {
  groupLaundryProductsByType,
  getUpdatedLaundryBundle,
  getBundleAccessories,
  getBundleDetails,
  getAccessoryProductIds,
  updateLaundryProducts,
  getLaundryDefaultProductIds
} from '../utils/product-utils';
import { useDataProducts } from './useDataProducts';
import { useProductCache } from './useProductsCache';
import { NONE_VALUE } from '../constants/index';

export const useLaundryEntryPoint = ({ bundle }) => {
  const isLaundryEntryPointEnabled = useConfigService('fs:isLaundryEntryPointEnabled') || false;
  const isAccessoriesFeatureEnabled = useConfigService('fs:isAccessoriesFeatureEnabled') || false;
  const isVariantSwap = useRef(false);
  const updateProductRef = useRef(null);
  const [productsState, setProductsState] = useState(null);
  const [productIds, setProductIds] = useState(null);
  const productsCacheRef = useRef({});
  const { saveProductsInCache, getProductFromCache } = useProductCache();

  const updatedBundle = getUpdatedLaundryBundle(bundle, isAccessoriesFeatureEnabled);

  const bundleDetailsRef = useRef(getBundleDetails(updatedBundle?.bundleSpecificationDetails));
  const bundleAccessories = getBundleAccessories(
    updatedBundle?.bundleSpecificationDetails
  );

  const defaultProductIds = getLaundryDefaultProductIds({
    components: updatedBundle?.bundleSpecificationDetails?.components,
    accessories: bundleAccessories
  });
  const itemIds = productIds || defaultProductIds;
  const skip = !isLaundryEntryPointEnabled || !itemIds || itemIds.length === 0;

  const { data, loading, error } = useDataProducts({
    itemIds,
    skip
  });

  const updateBundleProducts = ({ newProduct, currentItemId }) => {
    const updatedProducts = productsState?.products?.map((product) => {
      if (product.itemId === currentItemId) {
        return newProduct;
      }
      return product;
    });

    const newState = {
      ...productsState,
      products: updatedProducts || []
    };

    setProductsState(newState);
  };

  const updateAccessories = ({ newProducts, offeringSelected }) => {
    const newState = {
      ...productsState,
      accessories: {
        ...productsState?.accessories,
        products: newProducts,
        selectedVariant: offeringSelected
      }
    };

    setProductsState(newState);
  };

  const swapProduct = ({ currentItemId, newItemId }) => {
    isVariantSwap.current = true;
    const cachedProduct = getProductFromCache(newItemId);
    if (cachedProduct) {
      updateBundleProducts({
        newProduct: cachedProduct,
        currentItemId
      });
      return;
    }

    updateProductRef.current = { currentItemId, isAccessory: false };
    setProductIds([newItemId]);
  };

  const swapAccessory = ({ offeringName }) => {
    isVariantSwap.current = true;
    if (offeringName === NONE_VALUE) {
      const newState = {
        ...productsState,
        accessories: {
          ...productsState?.accessories,
          selectedVariant: NONE_VALUE
        }
      };
      setProductsState(newState);
      return;
    }
    const accessoryOffering = bundleAccessories.find((offering) => offering.offeringName === offeringName);
    if (!accessoryOffering) {
      return;
    }

    const newItemIds = accessoryOffering?.products?.map(({ defaultProductId }) => defaultProductId) || [];
    const cachedProducts = newItemIds.map(getProductFromCache).filter(Boolean);
    if (cachedProducts.length === newItemIds.length) {
      updateAccessories({
        newProducts: cachedProducts,
        offeringSelected: offeringName
      });
      return;
    }

    updateProductRef.current = { currentOfferingName: offeringName, isAccessory: true };
    setProductIds(newItemIds);
  };

  useEffect(() => {
    if (!data?.products) {
      return;
    }
    try {
      const updatedProducts = updateLaundryProducts({
        products: data?.products,
        bundleDetails: bundleDetailsRef.current,
        selectedAccessory: productsState?.accessories?.selectedVariant
      });

      if (updateProductRef.current) {
        const newProduct = {
          ...updatedProducts?.[0]
        };

        if (updateProductRef.current.isAccessory) {
          updateAccessories({
            newProducts: updatedProducts,
            offeringSelected: updateProductRef.current.currentOfferingName
          });
        } else {
          updateBundleProducts({
            newProduct,
            currentItemId: updateProductRef.current.currentItemId
          });
        }
        updateProductRef.current = null;
      } else {
        setProductsState(groupLaundryProductsByType(updatedProducts, bundleDetailsRef.current));
      }
      saveProductsInCache(updatedProducts);
    } catch (errorProducts) {
      setProductsState({});
    }

  }, [data]);

  const { products, accessories } = productsState || {};
  return {
    products: products || [],
    accessories: accessories || [],
    error,
    loading: !skip && (loading || productsState == null) && !error && !isVariantSwap?.current,

    // methods
    swapProduct,
    swapAccessory
  };
};